import { Schema, Effect } from "effect";
import { describeIntRange } from "../openapi-query.js";
import { HttpApiEndpoint, HttpApiError, HttpApiGroup } from "effect/http-api";
import { OpenApiClass } from "../openapi-class.js";
import { WorkplaceSourceFacts } from "./workplace-source-facts.js";
import {
  CompanyRegisterFactsQuery,
  CompanyRegisterFactsResponse,
} from "./company-register-facts.js";
import { UUID } from "../uuid.js";
export {
  CompanyRegisterFactsQuery,
  CompanyRegisterFactsResponse,
  CompanyRegisterObservation,
} from "./company-register-facts.js";
export {
  LegalEntityCodeLabel,
  LegalEntityIndustryFact,
  LegalEntitySourceFacts,
} from "./legal-entity-source-facts.js";

export { WorkplaceSourceFacts } from "./workplace-source-facts.js";

const NullableString = Schema.NullOr(Schema.String);
const NullableNumber = Schema.NullOr(Schema.Number);
const LinkedInSpecialtiesInput = Schema.Union([Schema.String, Schema.Array(Schema.String)]);

/** Parsed bounds from LinkedIn's human-readable company-size label. */
export class LinkedInEmployeeRange extends OpenApiClass<LinkedInEmployeeRange>(
  "LinkedInEmployeeRange"
)({
  min: Schema.optional(Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0)))),
  max: Schema.optional(Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0)))),
}) {}

export class LinkedInCompanyLocation extends OpenApiClass<LinkedInCompanyLocation>(
  "LinkedInCompanyLocation"
)({
  label: Schema.String,
  locationType: Schema.optional(Schema.String),
  isPrimary: Schema.optional(Schema.Boolean),
  address: Schema.optional(Schema.String),
  city: Schema.optional(Schema.String),
  region: Schema.optional(Schema.String),
  country: Schema.optional(Schema.String),
  directionsUrl: Schema.optional(Schema.String),
}) {}

export class LinkedInAffiliatedPage extends OpenApiClass<LinkedInAffiliatedPage>(
  "LinkedInAffiliatedPage"
)({
  canonicalUrl: Schema.String,
  name: Schema.String,
}) {}

/** A global LinkedIn handle can be linked to zero or more Bloodline legal entities. */
export class LinkedInLegalEntityLink extends OpenApiClass<LinkedInLegalEntityLink>(
  "LinkedInLegalEntityLink"
)({
  orgnr: Schema.String,
  companyName: NullableString,
  verifiedAt: NullableString,
}) {}

/** A bounded company snapshot captured from the LinkedIn company page currently open in the browser. */
export class LinkedInCompanySnapshot extends OpenApiClass<LinkedInCompanySnapshot>(
  "LinkedInCompanySnapshot"
)({
  canonicalUrl: Schema.String,
  name: Schema.String,
  tagline: Schema.optional(Schema.String),
  linkedInUrn: Schema.optional(Schema.String),
  websiteUrl: Schema.optional(Schema.String),
  industry: Schema.optional(Schema.String),
  numberOfEmployees: Schema.optional(
    Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0)))
  ),
  employeeRange: Schema.optional(Schema.String),
  employeeRangeBounds: Schema.optional(LinkedInEmployeeRange),
  headquarters: Schema.optional(Schema.String),
  locations: Schema.optional(Schema.Array(LinkedInCompanyLocation)),
  description: Schema.optional(Schema.String),
  overview: Schema.optional(Schema.String),
  companyType: Schema.optional(Schema.String),
  founded: Schema.optional(Schema.String),
  // Older installed extensions send a comma-separated string; the API normalizes it to an array.
  specialties: Schema.optional(LinkedInSpecialtiesInput),
  followerCount: Schema.optional(Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0)))),
  associatedMemberCount: Schema.optional(
    Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0)))
  ),
  pageVerified: Schema.optional(Schema.Boolean),
  pageVerifiedAt: Schema.optional(Schema.String),
  affiliatedPages: Schema.optional(Schema.Array(LinkedInAffiliatedPage)),
}) {}

/** Request made by the browser extension to persist a global LinkedIn company observation. */
export class CaptureLinkedInCompanyRequest extends OpenApiClass<CaptureLinkedInCompanyRequest>(
  "CaptureLinkedInCompanyRequest"
)({
  idempotencyKey: UUID,
  snapshot: LinkedInCompanySnapshot,
  orgnr: Schema.optional(Schema.String),
}) {}

/** Read-only capture preview, including the global company identity and field-level changes. */
export class PreviewLinkedInCompanyResponse extends OpenApiClass<PreviewLinkedInCompanyResponse>(
  "PreviewLinkedInCompanyResponse"
)({
  status: Schema.Literals(["new", "unchanged", "updated"]),
  sourceIdentityId: Schema.NullOr(UUID),
  orgnr: NullableString,
  legalEntityLinks: Schema.Array(LinkedInLegalEntityLink),
  changedFields: Schema.Array(Schema.String),
  previousSnapshot: Schema.NullOr(LinkedInCompanySnapshot),
}) {}

/** Completed observation capture. A retry with the same idempotency key returns the same observation id. */
export class CaptureLinkedInCompanyResponse extends OpenApiClass<CaptureLinkedInCompanyResponse>(
  "CaptureLinkedInCompanyResponse"
)({
  status: Schema.Literals(["new", "unchanged", "updated"]),
  sourceIdentityId: Schema.NullOr(UUID),
  orgnr: NullableString,
  legalEntityLinks: Schema.Array(LinkedInLegalEntityLink),
  changedFields: Schema.Array(Schema.String),
  previousSnapshot: Schema.NullOr(LinkedInCompanySnapshot),
  observationId: UUID,
}) {}

/** An immutable LinkedIn company observation, ordered by the time it was observed. */
export class LinkedInCompanyHistoryItem extends OpenApiClass<LinkedInCompanyHistoryItem>(
  "LinkedInCompanyHistoryItem"
)({
  observationId: UUID,
  observedAt: Schema.String,
  recordedAt: Schema.String,
  captureOrigin: Schema.Literals(["browser_capture", "legacy_linkedin_scraper"]),
  snapshot: LinkedInCompanySnapshot,
}) {}

export class LinkedInCompanyHistoryResponse extends OpenApiClass<LinkedInCompanyHistoryResponse>(
  "LinkedInCompanyHistoryResponse"
)({
  items: Schema.Array(LinkedInCompanyHistoryItem),
}) {}

const IndustryItemSchema = Schema.Struct({
  code: NullableString,
  text: NullableString,
  type: NullableString,
});

export const CompanyInfoSchema = Schema.Struct({
  valu8Id: Schema.Number,
  companyName: NullableString,
  companyType: NullableString,
  countryCode: NullableString,
  operationalStatus: NullableString,
  legalForm: NullableString,
  legalFormDetailed: NullableNumber,
  listingType: NullableString,
  registrationDate: NullableString,
  email: NullableString,
  phone: NullableString,
  vatNumber: NullableString,
  signatory: NullableString,
  previousNames: Schema.NullOr(Schema.Array(Schema.String)),
  businessDescriptionEng: NullableString,
  businessDescriptionOriginal: NullableString,
  industryCodes: Schema.NullOr(Schema.Array(IndustryItemSchema)),
  weightedGroupIndustryCodes: Schema.NullOr(Schema.Array(IndustryItemSchema)),
  hasReportedMissingUbo: Schema.Boolean,
  suspicionLevelUbo: NullableNumber,
  hasForeignUltimateParent: Schema.Boolean,
  hasGroupStructure: Schema.Boolean,
  keywords: Schema.NullOr(Schema.Array(Schema.String)),
  privateEquityOwner: NullableString,
  websiteSummary: NullableString,
  ultimateParentOrgNo: NullableString,
  ultimateParentCompanyName: NullableString,
  url: NullableString,
  additionalUrls: Schema.NullOr(Schema.Array(Schema.String)),
});

export const CompanyAddressSchema = Schema.Struct({
  street: NullableString,
  zipCode: NullableString,
  city: NullableString,
  county: NullableString,
  municipality: NullableString,
  countryCode: NullableString,
});

export const CompanyEmployeesSchema = Schema.Struct({
  year: Schema.Int,
  employeeCount: Schema.Int,
  isConsolidated: Schema.Boolean,
});

export const CompanyFinancialsSchema = Schema.Struct({
  fiscalYear: Schema.Int,
  isConsolidated: Schema.Boolean,
  fiscalYearEndDate: NullableString,
  lastReportQuarter: NullableString,
  currencyCode: NullableString,
  financialFigureScale: NullableString,
  fiscalYearLength: NullableNumber,
  fiscalYearEndMonth: NullableNumber,
  // Balance Sheet
  accountsPayable: NullableNumber,
  accountReceivables: NullableNumber,
  cashEquivalents: NullableNumber,
  currentFinancialAssets: NullableNumber,
  currentFinancialLiabilities: NullableNumber,
  goodwillAndIntangibleAssets: NullableNumber,
  inventories: NullableNumber,
  minorityInterest: NullableNumber,
  nonCurrentFinancialLiabilities: NullableNumber,
  nonCurrentFinancialReceivables: NullableNumber,
  otherCurrentOperatingAssets: NullableNumber,
  otherCurrentOperatingLiabilities: NullableNumber,
  otherNonCurrentOperatingAssets: NullableNumber,
  otherNonCurrentOperatingLiabilities: NullableNumber,
  postEmployeeLiabilities: NullableNumber,
  profitAndLossAccount: NullableNumber,
  propertyPlantAndEquipment: NullableNumber,
  shareCapital: NullableNumber,
  shareholderEquity: NullableNumber,
  totalAssets: NullableNumber,
  totalCurrentAssets: NullableNumber,
  totalCurrentLiabilities: NullableNumber,
  totalEquity: NullableNumber,
  totalNonCurrentAssets: NullableNumber,
  totalNonCurrentLiabilities: NullableNumber,
  // Profit and Loss
  administrativeExpenses: NullableNumber,
  amortisation: NullableNumber,
  costOfSales: NullableNumber,
  depreciation: NullableNumber,
  depreciationAmortisation: NullableNumber,
  ebit: NullableNumber,
  ebitda: NullableNumber,
  ebita: NullableNumber,
  ebitd: NullableNumber,
  financialExpenses: NullableNumber,
  financialIncome: NullableNumber,
  grossProfit: NullableNumber,
  netFinancialIncomeOrExpenses: NullableNumber,
  netProfit: NullableNumber,
  netSales: NullableNumber,
  otherExternalExpenses: NullableNumber,
  personelExpenses: NullableNumber,
  preTaxProfit: NullableNumber,
  rawMaterialsAndConsumables: NullableNumber,
  sellingExpenses: NullableNumber,
  totalRevenue: NullableNumber,
  // Cash Flow
  cashFlowBeforeFinancialActivites: NullableNumber,
  cashflowOperating: NullableNumber,
  netChangeInCash: NullableNumber,
  // Key Ratios
  cagr3YearsNetSales: NullableNumber,
  cagr5YearsNetSales: NullableNumber,
  cagr3YearsEbit: NullableNumber,
  cagr5YearsEbit: NullableNumber,
  cagr3YearsGrossProfit: NullableNumber,
  cagr5YearsGrossProfit: NullableNumber,
  cagr3YearsEbitda: NullableNumber,
  cagr5YearsEbitda: NullableNumber,
  cagr3YearsNumberOfEmployees: NullableNumber,
  cagr5YearsNumberOfEmployees: NullableNumber,
  cagr3YearsTotalAssets: NullableNumber,
  cagr5YearsTotalAssets: NullableNumber,
  cagr3YearsTotalCurrentAssets: NullableNumber,
  cagr5YearsTotalCurrentAssets: NullableNumber,
  dividends: NullableNumber,
  dps: NullableNumber,
  ev: NullableNumber,
  ebitdaGrowth: NullableNumber,
  ebitdaMargin: NullableNumber,
  ebitGrowth: NullableNumber,
  ebitMargin: NullableNumber,
  employeeGrowth: NullableNumber,
  eps: NullableNumber,
  evToEbit: NullableNumber,
  evToEbitda: NullableNumber,
  evToNetSales: NullableNumber,
  equityRatio: NullableNumber,
  grossProfitGrowth: NullableNumber,
  grossProfitMargin: NullableNumber,
  marketCap: NullableNumber,
  netCapex: NullableNumber,
  netDebt: NullableNumber,
  netDebtToEquity: NullableNumber,
  netDebtToEbit: NullableNumber,
  netDebtToEbitda: NullableNumber,
  netProfitGrowth: NullableNumber,
  netProfitMargin: NullableNumber,
  netSalesGrowth: NullableNumber,
  pe: NullableNumber,
  preTaxProfitMargin: NullableNumber,
  priceToBookValue: NullableNumber,
  retainedEarnings: NullableNumber,
  returnOnEquity: NullableNumber,
  rrk: NullableNumber,
  // Ultimate Parent
  ultimateParentDeprAmo: NullableNumber,
  ultimateParentEbit: NullableNumber,
  ultimateParentEbitda: NullableNumber,
  ultimateParentNetSales: NullableNumber,
  // Other
  chargesOutstanding: NullableNumber,
  shareIssuePriceEarnings: NullableNumber,
  shareIssuePriceEquity: NullableNumber,
  shareIssuePriceSales: NullableNumber,
  totalCharges: NullableNumber,
  totalShareIssueAmount: NullableNumber,
});

export const CompanyLinkedInSchema = Schema.Struct({
  name: NullableString,
  addresses: Schema.NullOr(Schema.Array(Schema.String)),
  classification: NullableString,
  classificationCode: NullableString,
  companyFounded: NullableString,
  companyType: NullableString,
  companyWebsite: NullableString,
  country: NullableString,
  description: NullableString,
  followers: NullableNumber,
  industry: NullableString,
  keywords: Schema.NullOr(Schema.Array(Schema.String)),
  numberOfEmployees: NullableNumber,
  sizeRange: NullableString,
  sourceUrl: NullableString,
});

export const CompanyProfileResponse = Schema.Struct({
  orgnr: Schema.String,
  company: CompanyInfoSchema,
  address: Schema.NullOr(CompanyAddressSchema),
  employees: Schema.Array(CompanyEmployeesSchema),
  financials: Schema.Array(CompanyFinancialsSchema),
  linkedin: Schema.NullOr(CompanyLinkedInSchema),
});

export const CompanyRefreshResponse = Schema.Struct({
  orgnr: Schema.String,
  status: Schema.Literals(["refreshed", "not_found", "failed"]),
  attemptedAt: Schema.String,
  completedAt: Schema.NullOr(Schema.String),
  error: Schema.NullOr(Schema.String),
});

export const CompanyWorkplaceItemSchema = Schema.Struct({
  cfarNumber: Schema.String,
  orgnr: Schema.String,
  companyName: NullableString,
  workplaceName: NullableString,
  visitAddress: NullableString,
  visitPostalCode: NullableString,
  visitCity: NullableString,
  municipalityCode: NullableString,
  municipalityName: NullableString,
  countyCode: NullableString,
  countyName: NullableString,
  statusCode: NullableString,
  statusText: NullableString,
  typeCode: NullableString,
  typeText: NullableString,
  representsCompany: Schema.NullOr(Schema.Boolean),
  employeeSizeClassCode: NullableString,
  employeeSizeClassText: NullableString,
  industryCodes: Schema.Array(Schema.String),
  sourceFacts: Schema.NullOr(WorkplaceSourceFacts).pipe(
    Schema.withDecodingDefaultType(Effect.sync(() => null)),
    Schema.withConstructorDefault(Effect.sync(() => null))
  ),
  startDate: NullableString,
  endDate: NullableString,
});

export const CompanyWorkplacesResponse = Schema.Struct({
  items: Schema.Array(CompanyWorkplaceItemSchema),
  totalCount: Schema.Int,
  offset: Schema.Int,
  limit: Schema.Int,
  hasMore: Schema.Boolean,
  complete: Schema.Boolean,
  lastSyncedAt: NullableString,
  nextRefreshAt: NullableString,
  source: Schema.Struct({
    key: Schema.Literal("scb_workplaces"),
    label: Schema.Literal("SCB Company Registry"),
  }),
});

const CompanyResolverCountry = Schema.String.pipe(
  Schema.check(Schema.isMinLength(2)),
  Schema.check(Schema.isMaxLength(2)),
  Schema.check(Schema.isPattern(/^[A-Z]{2}$/u))
);

export const ResolveCompanyRequest = Schema.Struct({
  query: Schema.String.pipe(
    Schema.check(Schema.isMinLength(1)),
    Schema.check(Schema.isPattern(/\S/u)),
    Schema.check(Schema.isPattern(/^[^\r\n]+$/u))
  ),
  country: Schema.optional(CompanyResolverCountry),
});

export const ResolveCompanyItem = Schema.Struct({
  valu8Id: Schema.Number,
  orgnr: NullableString,
  companyName: NullableString,
  countryCode: NullableString,
  url: NullableString,
  companyType: NullableString,
  operationalStatus: NullableString,
  registrationDate: NullableString,
  businessDescriptionEng: NullableString,
  businessDescriptionOriginal: NullableString,
  industryCodes: Schema.NullOr(Schema.Array(IndustryItemSchema)),
  keywords: Schema.NullOr(Schema.Array(Schema.String)),
});

export const ResolveCompanyResponse = Schema.Struct({
  items: Schema.Array(ResolveCompanyItem),
  resolution: Schema.Literals(["local", "valu8_import", "not_found"]),
  source: Schema.Literals(["valu8_mirror", "valu8"]),
});

const orgnrParam = Schema.String;

const getCompanyProfileEndpoint = HttpApiEndpoint.get("getCompanyProfile", "/companies/:orgnr", {
  params: { orgnr: orgnrParam },
  query: Schema.Struct({
    years: Schema.NumberFromString.pipe(
      Schema.check(Schema.isInt()),
      Schema.check(Schema.isGreaterThan(0)),
      describeIntRange({ minimum: 1 })
    ).pipe(
      Schema.withDecodingDefaultType(Effect.sync(() => 5)),
      Schema.withConstructorDefault(Effect.sync(() => 5))
    ),
  }),
  success: CompanyProfileResponse,
  error: [
    HttpApiError.BadRequestNoContent,
    HttpApiError.NotFoundNoContent,
    HttpApiError.InternalServerErrorNoContent,
  ],
});

const refreshCompanyEndpoint = HttpApiEndpoint.post("refreshCompany", "/companies/:orgnr/refresh", {
  params: { orgnr: orgnrParam },
  success: CompanyRefreshResponse,
  error: [
    HttpApiError.BadRequestNoContent,
    HttpApiError.ServiceUnavailableNoContent,
    HttpApiError.InternalServerErrorNoContent,
  ],
});

export const CompanyWorkplacesQuery = Schema.Struct({
  status: Schema.Literals(["active", "all"]).pipe(
    Schema.withDecodingDefaultType(Effect.sync(() => "active" as const)),
    Schema.withConstructorDefault(Effect.sync(() => "active" as const))
  ),
  offset: Schema.NumberFromString.pipe(
    Schema.check(Schema.isInt()),
    Schema.check(Schema.isGreaterThanOrEqualTo(0)),
    describeIntRange({ minimum: 0 })
  ).pipe(
    Schema.withDecodingDefaultType(Effect.sync(() => 0)),
    Schema.withConstructorDefault(Effect.sync(() => 0))
  ),
  limit: Schema.NumberFromString.pipe(
    Schema.check(Schema.isInt()),
    Schema.check(Schema.isGreaterThan(0)),
    Schema.check(Schema.isLessThanOrEqualTo(500)),
    describeIntRange({ minimum: 1, maximum: 500 })
  ).pipe(
    Schema.withDecodingDefaultType(Effect.sync(() => 100)),
    Schema.withConstructorDefault(Effect.sync(() => 100))
  ),
});

const getCompanyWorkplacesEndpoint = HttpApiEndpoint.get(
  "getCompanyWorkplaces",
  "/companies/:orgnr/workplaces",
  {
    params: { orgnr: orgnrParam },
    query: CompanyWorkplacesQuery,
    success: CompanyWorkplacesResponse,
    error: [
      HttpApiError.BadRequestNoContent,
      HttpApiError.NotFoundNoContent,
      HttpApiError.InternalServerErrorNoContent,
    ],
  }
);

const getCompanyRegisterFactsEndpoint = HttpApiEndpoint.get(
  "getCompanyRegisterFacts",
  "/companies/:orgnr/register-facts",
  {
    params: { orgnr: orgnrParam },
    query: CompanyRegisterFactsQuery,
    success: CompanyRegisterFactsResponse,
    error: [
      HttpApiError.BadRequestNoContent,
      HttpApiError.NotFoundNoContent,
      HttpApiError.InternalServerErrorNoContent,
    ],
  }
);

const resolveCompanyEndpoint = HttpApiEndpoint.post("resolveCompany", "/companies/resolve", {
  payload: ResolveCompanyRequest,
  success: ResolveCompanyResponse,
  error: [
    HttpApiError.BadRequestNoContent,
    HttpApiError.ServiceUnavailableNoContent,
    HttpApiError.InternalServerErrorNoContent,
  ],
});

const previewLinkedInCompanyEndpoint = HttpApiEndpoint.post(
  "previewLinkedInCompany",
  "/companies/linkedin/preview",
  {
    payload: Schema.Struct({
      snapshot: LinkedInCompanySnapshot,
      orgnr: Schema.optional(Schema.String),
    }),
    success: PreviewLinkedInCompanyResponse,
    error: [HttpApiError.BadRequestNoContent, HttpApiError.InternalServerErrorNoContent],
  }
);

const captureLinkedInCompanyEndpoint = HttpApiEndpoint.post(
  "captureLinkedInCompany",
  "/companies/linkedin/capture",
  {
    payload: CaptureLinkedInCompanyRequest,
    success: CaptureLinkedInCompanyResponse,
    error: [
      HttpApiError.BadRequestNoContent,
      HttpApiError.ConflictNoContent,
      HttpApiError.InternalServerErrorNoContent,
    ],
  }
);

const getCompanyLinkedInHistoryEndpoint = HttpApiEndpoint.get(
  "getCompanyLinkedInHistory",
  "/companies/:orgnr/linkedin/history",
  {
    params: { orgnr: orgnrParam },
    success: LinkedInCompanyHistoryResponse,
    error: [HttpApiError.BadRequestNoContent, HttpApiError.InternalServerErrorNoContent],
  }
);

export const CompaniesV3Group = HttpApiGroup.make("companiesV3")
  .add(getCompanyProfileEndpoint)
  .add(refreshCompanyEndpoint)
  .add(getCompanyWorkplacesEndpoint)
  .add(getCompanyRegisterFactsEndpoint)
  .add(resolveCompanyEndpoint)
  .add(previewLinkedInCompanyEndpoint)
  .add(captureLinkedInCompanyEndpoint)
  .add(getCompanyLinkedInHistoryEndpoint);
